import { Component } from 'preact';
import { motion, AnimatePresence } from 'framer-motion';
import {
  GOLD, ACCENT_GOLD, PARCHMENT, TEXT_MUTED,
  CONTENT_BG_DEFAULT, PANEL_BG, FourCorners,
} from '../lib/medievalTheme';
import VikingOrnament from './VikingOrnament';
import AchievementOrnamentIcon from './AchievementOrnamentIcon';
import { KeystoneIcon } from './ui/icons';
import { playUI, UI } from '../utils/arena/uiSounds';
import { ACHIEVEMENT_KEYSTONE_REWARD } from '../utils/arena/achievements';
import { t } from '../localization/t';

// Click-to-claim celebration overlay. Renders over the entire app
// (z-[300]) the moment the player clicks an unclaimed achievement.
// Three phases:
//   reveal. Medallion zooms in from center with a particle burst,
//            achievement title fades up, ACHIEVEMENT sound plays
//   payout. After the server confirms, the keystone counter inside
//            the overlay ticks from 0 → +1; a PURCHASE-style chime
//            seals the moment
//   exit. Fade out on click anywhere (or the button)
//
// Visual language matches the other ceremonial panels in the app
// (FirstPlayerDiceModal, ArenaStarterPicker): textured stone background
// blended over a dark base, multi-layer boxShadow stack, faded heraldic
// centerpiece, FourCorners + flanking knots, and gold-gradient text on
// the headline. No emoji glyphs. The hero medallion renders a Borre
// knot ornament through VikingOrnament's mask system, keeping the
// design system cohesive.

const PARTICLE_COUNT = 26;

// Enter choreography: the panel frame springs in, then its content
// lands top-to-bottom in ~100ms steps (band → medallion → title →
// footer cluster) with the standard opacity/y/blur reveal. One
// orchestrated moment instead of everything arriving at once.
const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.08 } },
};
const staggerItem = {
  hidden: { opacity: 0, y: 12, filter: 'blur(4px)' },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.4, ease: [0.2, 0, 0, 1] },
  },
};

export default class AchievementClaimOverlay extends Component {
  state = {
    phase: 'reveal',          // reveal → payout → done
    payoutResult: null,       // server response once claim resolves
    counterStarted: false,    // gates the +1 keystone tick-up
  };

  _mountedAt = Date.now();

  componentDidMount() {
    // Reveal sting on mount, slightly softer than default so the payout
    // PURCHASE chime can layer on top without clipping.
    playUI(UI.ACHIEVEMENT, { volume: 0.85 });
    this._fireClaim();
  }

  componentWillUnmount() {
    this._unmounted = true;
  }

  _fireClaim = async () => {
    const { onClaim } = this.props;
    if (typeof onClaim !== 'function') return;
    try {
      const result = await onClaim();
      if (this._unmounted) return;
      this.setState({ payoutResult: result }, this._maybeStartPayout);
    } catch (err) {
      console.error('[AchievementClaimOverlay] claim failed:', err);
      if (this._unmounted) return;
      this.setState({ payoutResult: { claimed: false } }, this._maybeStartPayout);
    }
  };

  // Payout fires once the server has responded AND the reveal has had
  // ~950ms to land (the staggered enter settles around 800ms).
  // Otherwise the +1 keystone ticks before the medallion has settled,
  // which trashes the rhythm.
  _maybeStartPayout = () => {
    if (this.state.counterStarted) return;
    if (!this.state.payoutResult) return;
    const elapsed = Date.now() - this._mountedAt;
    const wait = Math.max(0, 950 - elapsed);
    setTimeout(() => {
      if (this._unmounted) return;
      this.setState({ phase: 'payout', counterStarted: true });
      playUI(UI.PURCHASE, { volume: 0.7, debounce: 0 });
    }, wait);
  };

  handleDismiss = () => {
    if (this._unmounted) return;
    playUI(UI.CLICK);
    this.props.onDismiss?.();
  };

  render() {
    const { achievement } = this.props;
    const { phase } = this.state;
    if (!achievement) return null;

    return (
      <motion.div
        className="fixed inset-0 z-[300] flex items-center justify-center cursor-pointer"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.22 }}
        onClick={this.handleDismiss}
        style={{
          // Match the FirstPlayerDiceModal scrim. A radial darkening
          // toward the edges that frames the panel without going to
          // pure black at center.
          background: 'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.88) 100%)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
        }}
      >
        <ParticleBurst />

        <motion.div
          className="relative flex flex-col items-center px-12 py-9"
          initial={{ scale: 0.7, opacity: 0, y: '2cqw' }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.94, opacity: 0, transition: { duration: 0.18, ease: 'easeIn' } }}
          transition={{ type: 'spring', stiffness: 220, damping: 22, mass: 0.9 }}
          style={{
            minWidth: '34cqw',
            maxWidth: '42cqw',
            // Textured stone wash over the dark panel base. The blend
            // mode pair is the same as FirstPlayerDiceModal. The
            // overlay layer brings the stone/chisel/scratches grain;
            // the normal layer keeps the deep parchment-black behind it.
            background: `${CONTENT_BG_DEFAULT}, ${PANEL_BG}`,
            backgroundBlendMode: 'overlay, normal',
            border: `1px solid ${GOLD} 0.6)`,
            borderRadius: 'var(--radius-lg)',
            // Layered shadow stack: outer drop for depth, gold glow
            // for ceremony, inset gold rim for chiseled edge, inset
            // darkening for vignette. Same recipe as the dice modal.
            boxShadow: [
              '0 32px 100px rgba(0,0,0,0.75)',
              `0 0 80px ${GOLD} 0.22)`,
              `inset 0 0 0 1px ${GOLD} 0.25)`,
              `inset 0 0 60px rgba(0,0,0,0.55)`,
            ].join(', '),
            // Establish a stacking context so the corner ornaments
            // and the medallion's glow stay clipped to this panel.
            isolation: 'isolate',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <FourCorners color={`${GOLD} 0.65)`} />

          <motion.div
            className="flex flex-col items-center w-full"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
          {/* Header banner. Single centered band with horizontal gold
              rules above and below. Its own rules are the only divider
              this panel gets: the ornamented rune dividers that used to
              flank the content read as two extra centerpieces fighting
              the medallion, so spacing does the separating instead. */}
          <motion.div variants={staggerItem} className="flex items-center justify-center" style={{ marginBottom: '1.4cqw' }}>
            <div
              style={{
                display: 'inline-block',
                fontSize: '0.8125cqw',
                letterSpacing: '0.5cqw',
                color: `${PARCHMENT} 0.92)`,
 fontWeight: 700,
 padding: '0.3cqw 1.5cqw',
 borderTop: `1px solid ${GOLD} 0.45)`,
                borderBottom: `1px solid ${GOLD} 0.45)`,
                background: `linear-gradient(180deg, transparent, ${GOLD} 0.08), transparent)`,
                textShadow: '0 1px 2px rgba(0,0,0,0.7)',
              }}
            >
              {t('Achievement Unlocked')}
            </div>
          </motion.div>

          {/* Hero medallion. Outer ring is a heavy gold-rimmed coin
              face; inside we drop a VikingOrnament Borre knot via its
              `medallion` variant so the heraldic sigil glows from the
              center out, no emoji glyph involved. Pulsing halo loops
              behind the whole thing. */}
          <motion.div
            variants={staggerItem}
            className="relative flex items-center justify-center"
            style={{ width: '7.5cqw', height: '7.5cqw' }}
          >
            {/* Pulsing halo. Soft gold radial that breathes while the
                overlay is up. */}
            <motion.div
              className="absolute inset-0 rounded-full pointer-events-none"
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: [1, 1.22, 1], opacity: [0.55, 0.22, 0.55] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
              style={{
                background: `radial-gradient(circle, ${GOLD} 0.55) 0%, ${GOLD} 0) 70%)`,
                filter: 'blur(10px)',
              }}
            />
            {/* Coin face. Embossed gold rim, stone-tinted recessed
                interior, dual inset shadows give it the chiseled look
                of the playmat medallions. */}
            {/* Coin fires on its stagger slot (variants inherit from
                the container), so the rotate-settle reads as the coin
                being struck into place rather than pre-arriving under
                an invisible wrapper. */}
            <motion.div
              className="relative rounded-full overflow-hidden"
              variants={{
                hidden: { rotate: -10, scale: 0.82, opacity: 0 },
                visible: {
                  rotate: 0,
                  scale: 1,
                  opacity: 1,
                  transition: { duration: 0.55, ease: [0.18, 0.9, 0.32, 1] },
                },
              }}
              style={{
                width: '6.25cqw',
                height: '6.25cqw',
                background: 'radial-gradient(circle at 40% 35%, rgba(60, 44, 18, 0.95) 0%, rgba(20, 14, 6, 0.95) 70%)',
                border: `1.5px solid ${ACCENT_GOLD}`,
                boxShadow: [
                  `inset 0 0 0 2px ${GOLD} 0.35)`,
                  `inset 0 2px 4px rgba(255, 220, 140, 0.18)`,
                  `inset 0 -3px 6px rgba(0, 0, 0, 0.55)`,
                  `0 0 28px ${GOLD} 0.35)`,
                ].join(', '),
                isolation: 'isolate',
              }}
            >
              {/* Inner glyph. The achievement-specific Viking
                  ornament. Same gold-tinted mask render the panel
                  row uses, so the unlock moment and the panel
                  share a glyph. Falls back to a generic Borre-knot
                  medallion if for any reason the per-id asset is
                  missing (asset bundling skew during dev, etc.). */}
              {achievement.id ? (
                <AchievementOrnamentIcon
                  achievementId={achievement.id}
                  color="rgba(244, 215, 136, 0.95)"
                  opacity={0.92}
                  style={{
                    position: 'absolute',
                    inset: '10%',
                    width: 'auto',
                    height: 'auto',
                  }}
                />
              ) : (
                <VikingOrnament
                  ornament="borreKnot"
                  variant="medallion"
                  color="rgba(244, 215, 136, 0.95)"
                  opacity={0.88}
                />
              )}
            </motion.div>
          </motion.div>

          {/* Title + description. Achievement name uses the gold
              gradient text fill from the dice modal so the headline
              reads as polished metal rather than flat text. */}
          <motion.div
            variants={staggerItem}
            className="flex flex-col items-center text-center"
            style={{ maxWidth: '28cqw', marginTop: '1.25cqw', gap: '0.5cqw' }}
          >
            <h2
              className="arena-heading font-bold"
              style={{
                fontSize: '1.75cqw',
                letterSpacing: '0.04em',
                lineHeight: 1.1,
                textWrap: 'balance',
                background: 'linear-gradient(180deg, #fbe9b8 0%, #e8c466 55%, #b9893a 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.7))',
              }}
            >
              {achievement.name}
            </h2>
            <p
              className="text-[0.8125cqw]"
              style={{ color: `${PARCHMENT} 0.78)`, lineHeight: 1.45, fontStyle: 'italic', textWrap: 'pretty' }}
            >
              {achievement.description}
            </p>
          </motion.div>

          {/* Footer cluster. Reward slot + button land together as
              the last stagger beat. The pill slot reserves space so
              the layout stays still through reveal → payout. */}
          <motion.div
            variants={staggerItem}
            className="flex flex-col items-center w-full"
            style={{ marginTop: '1.75cqw' }}
          >
          <div className="flex items-center justify-center" style={{ minHeight: '2.75cqw' }}>
            <AnimatePresence>
              {phase === 'payout' ? (
                <motion.div
                  key="reward"
                  initial={{ scale: 0.4, opacity: 0, y: 12 }}
                  animate={{ scale: 1, opacity: 1, y: 0 }}
                  transition={{ type: 'spring', stiffness: 320, damping: 18 }}
                  className="flex items-center gap-2.5 px-5 py-1.5 rounded-full"
                  style={{
                    // Solid dark base so the textured panel doesn't
                    // bleed through. A subtle top→bottom gradient adds
                    // depth without going translucent. Outer gold glow
                    // + top highlight + bottom inset darkening give it
                    // the same struck-coin chiseling as the medallion.
                    background: 'linear-gradient(180deg, rgba(34, 26, 14, 0.99) 0%, rgba(16, 12, 6, 0.99) 100%)',
                    border: `1.5px solid ${ACCENT_GOLD}`,
                    boxShadow: [
                      `0 0 22px ${GOLD} 0.45)`,
                      'inset 0 1px 0 rgba(255, 220, 140, 0.32)',
                      'inset 0 -2px 4px rgba(0, 0, 0, 0.55)',
                    ].join(', '),
                  }}
                >
                  <KeystoneIcon size={18} />
                  <span
                    className="text-[1.125cqw] font-bold tabular-nums"
                    style={{
                      background: 'linear-gradient(180deg, #fbe9b8 0%, #e8c466 60%, #b9893a 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))',
                    }}
                  >
                    +{ACHIEVEMENT_KEYSTONE_REWARD}
                  </span>
                  <span
                    className="text-[0.625cqw] font-semibold"
                    style={{ color: '#c9a86a' }}
                  >
                    {t('Keystone')}
                  </span>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>

          <motion.button
            type="button"
            onClick={this.handleDismiss}
            whileTap={{ scale: 0.96 }}
            className="cursor-pointer"
            style={{
              // Phase flip (reveal → payout) retints the button; list
              // the properties that actually change instead of
              // transition-all so the tap scale stays framer's alone.
              transition: 'color 200ms ease, border-color 200ms ease, box-shadow 200ms ease, opacity 200ms ease, text-shadow 200ms ease',
              marginTop: '0.5cqw',
              padding: '0.55cqw 1.75cqw',
              // Solid dark base; same recipe as the reward pill so the
              // two read as a related pair sitting at the bottom of
              // the panel. Pre-payout the gold tones dim slightly so
              // the eye is drawn to the medallion first.
              background: 'linear-gradient(180deg, rgba(28, 22, 14, 0.99) 0%, rgba(14, 10, 6, 0.99) 100%)',
              border: `1.5px solid ${GOLD} ${phase === 'payout' ? '0.6' : '0.32'})`,
 borderRadius: 'var(--radius-md)',
 color: phase === 'payout' ? ACCENT_GOLD : TEXT_MUTED,
 fontSize: '0.75cqw',
 fontWeight: 700,
 textShadow: phase === 'payout' ? `0 0 10px ${GOLD} 0.4)` : 'none',
              boxShadow: phase === 'payout'
                ? [
                    `0 0 18px ${GOLD} 0.28)`,
                    'inset 0 1px 0 rgba(255, 220, 140, 0.22)',
                    'inset 0 -2px 4px rgba(0, 0, 0, 0.55)',
                  ].join(', ')
                : 'inset 0 -2px 4px rgba(0, 0, 0, 0.45)',
              opacity: phase === 'payout' ? 1 : 0.7,
            }}
          >
            {t('Continue')}
          </motion.button>
          </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>
    );
  }
}

// Decorative motes radiating outward from the medallion. Each one
// gets a random angle/distance/delay so the burst feels organic.
// Sized in cqw so the spread scales with the canvas.
function ParticleBurst() {
  const motes = Array.from({ length: PARTICLE_COUNT }, (_, i) => {
    const angle = (i / PARTICLE_COUNT) * Math.PI * 2 + Math.random() * 0.4;
    const distance = 8 + Math.random() * 6;
    const x = Math.cos(angle) * distance;
    const y = Math.sin(angle) * distance;
    const delay = Math.random() * 0.18;
    return { x, y, delay, key: i };
  });
  return (
    <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
      {motes.map((m) => (
        <motion.div
          key={m.key}
          className="absolute rounded-full"
          initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
          animate={{ x: `${m.x}cqw`, y: `${m.y}cqw`, opacity: 0, scale: 0.25 }}
          transition={{ duration: 1.35, delay: m.delay, ease: [0.22, 0.8, 0.36, 1] }}
          style={{
            width: '0.5cqw',
            height: '0.5cqw',
            background: ACCENT_GOLD,
            boxShadow: `0 0 10px ${GOLD} 0.85)`,
          }}
        />
      ))}
    </div>
  );
}
